import { useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useMediaLoadingDispatch } from "../../../../contexts/mediaLoading.context";
import { useNotificationDispatch } from "../../../../contexts/notification.context";
import { useUserState } from "../../../../contexts/user.context";
import { useCreativeState } from "../../../../contexts/creative.context";
import { delete_creative } from "../../../../helpers/delete_creative";
import { duplicate_creative } from "../../../../helpers/duplicate_creative";
import { GET_EXISTING_CREATIVE, LIST_CREATIVES, LIST_CREATIVES_FILTERS } from "../../../../graphql/queries";
import { logger } from "../../../../libs/logger";
import * as mutations from "../../../../graphql/mutations";
import { CreativeActionsProps, CreativesTypes } from "../types";

// Utility functions for creative actions
export function useCreativeActions({ getPreviews, deleteCreativeModal, setDeleteCreativeModal, advertiserId, queryVariables }: CreativeActionsProps) {
  const [creatives, setCreatives] = useState<CreativesTypes[]>([]);
  const user = useUserState();
  const creative = useCreativeState();
  const notificationDispatch = useNotificationDispatch();
  const mediaLoadingDispatch = useMediaLoadingDispatch();

  const [saveNewCreative] = useMutation(mutations.SAVE_NEW_CREATIVE);
  const [deleteCreative] = useMutation(mutations.DELETE_CREATIVE);

  const [updateAllCreatives] = useLazyQuery(LIST_CREATIVES_FILTERS, {
    variables: queryVariables,
    fetchPolicy: "network-only",
    onCompleted: async (creativeData) => {
      setCreatives(await getPreviews(creativeData.readAllCreativesAdvertiserFilters.data));
    },
    onError: (error) => {
      logger({ error, user, context: { origin: "CreativeActions_LIST_CREATIVES_FILTERS" } });
    },
  });

  const [getOneCreativeData] = useLazyQuery(GET_EXISTING_CREATIVE, {
    fetchPolicy: "network-only",
    onCompleted: async (creative_data: any) => {
      return creative_data;
    },
    onError: (error) => {
      logger({ error, user, context: { origin: "CreativeActions_GET_EXISTING_CREATIVE" } });
    },
  });

  const [getAllCreativesData] = useLazyQuery(LIST_CREATIVES, {
    variables: { teamId: user.teamId, advertiserId: advertiserId },
    fetchPolicy: "network-only",
    onCompleted: async (allCreativesData) => {
      return allCreativesData;
    },
    onError: (error) => {
      logger({ error, user, context: { origin: "CreativeActions_LIST_CREATIVES" } });
    },
  });

  const handleDuplicateCreative = async (e: any, existingCreativeId: string) => {
    e.preventDefault();
    const creative_data = await getOneCreativeData({ variables: { id: existingCreativeId, teamId: user.teamId } });
    await duplicate_creative(creative_data.data, creative_data.data.readOneCreativeId.id, user, mediaLoadingDispatch, notificationDispatch, saveNewCreative);
    updateAllCreatives();
  };

  const handleDeleteCreative = async (creativeId: any) => {
    let creative_data = await getOneCreativeData({ variables: { id: creativeId, teamId: user.teamId } });
    await delete_creative({ id: creative_data.data.readOneCreativeId.id, teamId: user.teamId }, deleteCreative, mediaLoadingDispatch, notificationDispatch);
    updateAllCreatives();
  };

  const openDeleteCreativeModal = async (e: any, creativeId: string) => {
    e.preventDefault();
    setDeleteCreativeModal({ creative: creativeId, open: !deleteCreativeModal.open });
  };

  return { handleDuplicateCreative, openDeleteCreativeModal, handleDeleteCreative, updateAllCreatives, saveNewCreative, deleteCreative, getOneCreativeData, getAllCreativesData };
}
